import React, { useEffect, useState } from 'react';
import { auditFetch, buildAuditHeaders } from '../utils/auditFetch';
import '../styles/Dashboard.css';

const emptyMedication = {
  name: '',
  dose: '',
  frequency: '',
  duration: '',
  instructions: '',
  warnings: '',
};

const DigitalPrescriptionPanel = ({
  appointmentId,
  patient,
  doctor,
  user,
}) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [medications, setMedications] = useState([{ ...emptyMedication }]);
  const [generalIndications, setGeneralIndications] = useState('');
  const [generalWarnings, setGeneralWarnings] = useState('');
  const [prescriptions, setPrescriptions] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!appointmentId) return;
    if (user.demoMode) {
      auditFetch(`${backendUrl}/api/demo/presentation`, {}, user)
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error('No se pudo cargar presentacion'))))
        .then((data) => {
          const demoPrescriptions = (data.prescriptions || []).filter((prescription) => prescription.appointmentId === appointmentId);
          setPrescriptions(demoPrescriptions);
        })
        .catch((error) => {
          console.error('Error loading demo prescriptions:', error);
          setStatusMessage('No se pudieron cargar recetas de presentacion.');
        });
      return;
    }

    auditFetch(`${backendUrl}/api/prescriptions/appointment/${appointmentId}`, {}, user)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('No se pudieron cargar recetas'))))
      .then((data) => setPrescriptions(Array.isArray(data.prescriptions) ? data.prescriptions : []))
      .catch((error) => {
        console.error('Error loading prescriptions:', error);
        setStatusMessage('No se pudieron cargar recetas previas.');
      });
  }, [appointmentId, backendUrl, user]);

  const updateMedication = (index, field, value) => {
    setMedications((current) => current.map((medication, currentIndex) => (
      currentIndex === index ? { ...medication, [field]: value } : medication
    )));
  };

  const addMedication = () => {
    setMedications((current) => [...current, { ...emptyMedication }]);
  };

  const removeMedication = (index) => {
    setMedications((current) => (
      current.length === 1 ? [{ ...emptyMedication }] : current.filter((_, currentIndex) => currentIndex !== index)
    ));
  };

  const openPdf = (prescription) => {
    if (user.demoMode) {
      setStatusMessage(`Modo presentacion: receta ficticia ${prescription.verificationCode} sin PDF real.`);
      return;
    }

    const url = `${backendUrl}/api/prescriptions/${prescription.id}/pdf`;
    fetch(url, { headers: buildAuditHeaders(user) })
      .then((res) => {
        if (!res.ok) throw new Error('No se pudo generar el PDF');
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      })
      .catch((error) => {
        console.error('Error opening prescription PDF:', error);
        setStatusMessage('No se pudo abrir el PDF de la receta.');
      });
  };

  const savePrescription = async () => {
    const cleanMedications = medications.filter((medication) => medication.name.trim());
    if (cleanMedications.length === 0) {
      setStatusMessage('Agrega al menos un medicamento.');
      return;
    }

    setIsSaving(true);
    setStatusMessage('');
    try {
      if (user.demoMode) {
        const demoPrescription = {
          id: `demo-rx-local-${Date.now()}`,
          verificationCode: `RX-DEMO-${Date.now().toString().slice(-5)}`,
          appointmentId,
          patient,
          doctor,
          medications: cleanMedications,
          generalIndications,
          generalWarnings,
          createdAt: new Date().toISOString(),
          demoMode: true,
        };
        setPrescriptions((current) => [demoPrescription, ...current]);
        setStatusMessage(`Receta guardada. Codigo: ${demoPrescription.verificationCode}. Usa el boton PDF en el historial para imprimir o descargar.`);
        return;
      }

      const response = await auditFetch(`${backendUrl}/api/prescriptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointmentId,
          patient,
          doctor,
          medications: cleanMedications,
          generalIndications,
          generalWarnings,
        }),
      }, user);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo guardar la receta');
      setPrescriptions((current) => [data.prescription, ...current]);
      setStatusMessage(`Receta guardada. Codigo: ${data.prescription.verificationCode}. Usa el boton PDF en el historial para imprimir o descargar.`);
    } catch (error) {
      console.error('Error saving prescription:', error);
      setStatusMessage(error.message || 'No se pudo emitir la receta.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="digital-prescription-panel">
      <div className="clinical-files-header">
        <div>
          <h3>Receta medica digital</h3>
          <p>Medicamentos, indicaciones, firma y PDF con QR de verificacion.</p>
        </div>
        <span>{prescriptions.length} receta(s)</span>
      </div>

      <div className="prescription-medications">
        {medications.map((medication, index) => (
          <div className="prescription-medication-card" key={`med-${index}`}>
            <div className="prescription-medication-title">
              <strong>Medicamento {index + 1}</strong>
              <button type="button" onClick={() => removeMedication(index)}>Quitar</button>
            </div>
            <input value={medication.name} onChange={(event) => updateMedication(index, 'name', event.target.value)} placeholder="Nombre del medicamento" />
            <div className="prescription-grid">
              <input value={medication.dose} onChange={(event) => updateMedication(index, 'dose', event.target.value)} placeholder="Dosis" />
              <input value={medication.frequency} onChange={(event) => updateMedication(index, 'frequency', event.target.value)} placeholder="Frecuencia" />
              <input value={medication.duration} onChange={(event) => updateMedication(index, 'duration', event.target.value)} placeholder="Duracion" />
            </div>
            <textarea value={medication.instructions} onChange={(event) => updateMedication(index, 'instructions', event.target.value)} placeholder="Indicaciones especificas" rows={2} />
            <textarea value={medication.warnings} onChange={(event) => updateMedication(index, 'warnings', event.target.value)} placeholder="Advertencias del medicamento" rows={2} />
          </div>
        ))}
      </div>

      <button type="button" className="btn btn-secondary full-width" onClick={addMedication}>
        Agregar medicamento
      </button>

      <div className="prescription-notes">
        <textarea value={generalIndications} onChange={(event) => setGeneralIndications(event.target.value)} placeholder="Indicaciones generales para el paciente" rows={2} />
        <textarea value={generalWarnings} onChange={(event) => setGeneralWarnings(event.target.value)} placeholder="Advertencias generales y signos de alarma" rows={2} />
      </div>

      <button type="button" className="btn btn-primary full-width" onClick={savePrescription} disabled={isSaving}>
        {isSaving ? 'Guardando...' : 'Guardar receta'}
      </button>
      {statusMessage && <p className="clinical-files-message">{statusMessage}</p>}

      {prescriptions.length > 0 && (
        <div className="prescription-history">
          <strong>Recetas emitidas</strong>
          {prescriptions.slice(0, 4).map((prescription) => (
            <div key={prescription.id} className="prescription-history-item">
              <span>{new Date(prescription.createdAt).toLocaleString('es-EC')} · {prescription.medications.length} medicamento(s)</span>
              <button type="button" onClick={() => openPdf(prescription)}>PDF</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DigitalPrescriptionPanel;
